/*
 * @Description:
 * @Date: 2024-10-28 18:07:07
 */
import type { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios';
import { ContentTypeEnum } from './enums';

import axios from 'axios';
import { AxiosCanceler } from './axios-cancel';
import { isFunction } from '@/utils/is';
import { cloneDeep } from 'lodash';
import { getFileNameFromUrl } from './url';
import type { RequestOptions, CreateAxiosOptions } from './types';
// import { ContentTypeEnum } from '/@/enums/httpEnum';

export * from './axios-transform';

/**
 * @description:  axios模块
 */
export class VAxios {
  private axiosInstance: AxiosInstance;
  private options: CreateAxiosOptions;

  constructor(options: CreateAxiosOptions) {
    this.options = options;
    this.axiosInstance = axios.create(options);
    this.setupInterceptors();
  }

  /**
   * @description:  创建axios实例
   */
  private createAxios(config: CreateAxiosOptions): void {
    this.axiosInstance = axios.create(config);
  }

  private getTransform() {
    const { transform } = this.options;
    return transform;
  }

  getAxios(): AxiosInstance {
    return this.axiosInstance;
  }

  /**
   * @description: 重新配置axios
   */
  configAxios(config: CreateAxiosOptions) {
    if (!this.axiosInstance) {
      return;
    }
    this.createAxios(config);
  }

  /**
   * @description: 设置通用header
   */
  setHeader(headers: any): void {
    if (!this.axiosInstance) {
      return;
    }
    Object.assign(this.axiosInstance.defaults.headers, headers);
  }

  /**
   * @description: 拦截器配置
   */
  private setupInterceptors() {
    const transform = this.getTransform();
    if (!transform) {
      return;
    }
    const {
      requestInterceptors,
      requestInterceptorsCatch,
      responseInterceptors,
      responseInterceptorsCatch
    } = transform;

    const axiosCanceler = new AxiosCanceler();

    // 请求拦截器配置处理
    this.axiosInstance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const {
          headers: { ignoreCancelToken } = { ignoreCancelToken: false }
        } = config;
        !ignoreCancelToken && axiosCanceler.addPending(config);
        if (requestInterceptors && isFunction(requestInterceptors)) {
          config = requestInterceptors(config);
        }
        return config;
      },
      undefined
    );

    // 请求拦截器错误捕获
    requestInterceptorsCatch &&
      isFunction(requestInterceptorsCatch) &&
      this.axiosInstance.interceptors.request.use(
        undefined,
        requestInterceptorsCatch
      );

    // 响应结果拦截器response处理
    this.axiosInstance.interceptors.response.use((res: AxiosResponse<any>) => {
      res && axiosCanceler.removePending(res.config);
      if (responseInterceptors && isFunction(responseInterceptors)) {
        res = responseInterceptors(res);
      }
      return res;
    }, undefined);

    // 响应结果拦截器错误捕获
    responseInterceptorsCatch &&
      isFunction(responseInterceptorsCatch) &&
      this.axiosInstance.interceptors.response.use(
        undefined,
        responseInterceptorsCatch
      );
  }

  /**
   * @description:   请求方法
   */
  request<T = any>(
    config: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<T> {
    let conf: AxiosRequestConfig = cloneDeep(config);
    const transform = this.getTransform();
    const { requestOptions } = this.options;
    const opt: RequestOptions = Object.assign({}, requestOptions, options);
    const { beforeRequestHook, requestCatch, transformRequestData } =
      transform || {};
    if (beforeRequestHook && isFunction(beforeRequestHook)) {
      conf = beforeRequestHook(conf, opt);
    }
    return new Promise((resolve, reject) => {
      this.axiosInstance
        .request<any, AxiosResponse | Result<any>>(conf) // AxiosResponse<Result>
        .then((resp: AxiosResponse) => {
          if (transformRequestData && isFunction(transformRequestData)) {
            const ret = transformRequestData(resp, opt) as any;
            return resolve(ret);
          }
          resolve(resp as unknown as Promise<T>);
        })
        .catch((e: AxiosResponse) => {
          if (requestCatch && isFunction(requestCatch)) {
            reject(requestCatch(e, opt));
            return;
          }
          reject(e);
        });
    });
  }
  /**
   * @description:  File Upload
   */
  uploadFile<T = any>(config: AxiosRequestConfig, options?: RequestOptions) {
    const formData = new window.FormData();
    Object.keys(config.params).forEach(key => {
      if (!config.params) return;
      const value = config.params[key];
      if (Array.isArray(value)) {
        value.forEach(item => {
          formData.append(`${key}[]`, item);
        });
        return;
      }
      formData.append(key, config.params[key]);
    });

    return this.request<T>(
      {
        method: 'POST',
        ...config,
        params: formData
      },
      {
        ...options,
        // 不可修改
        headers: {
          'Content-type': ContentTypeEnum.FORM_DATA,
          ignoreCancelToken: true
        }
      }
    );
  }
  /**
   * @description:  File Download
   */
  async download(config: AxiosRequestConfig, options?: RequestOptions) {
    const resp = await this.request<AxiosResponse<Blob>>(
      {
        method: 'POST',
        timeout: 0,
        ...config,
        responseType: 'blob'
      },
      {
        isShowSuccessMessage: true,
        successMessageText: '下载成功！',
        ...options,
        isTransformRequestResult: false // 不可修改
      }
    );
    const contentDisposition = resp.headers['content-disposition'];
    let filename = options?.downloadFileName;
    if (!filename) {
      if (contentDisposition) {
        // 优先使用filename*参数，如果不存在则使用filename参数
        const filenameStarMatch = contentDisposition.match(
          /filename\*=(?:UTF-8'')?([^;]+)/i
        );
        if (filenameStarMatch && filenameStarMatch[1]) {
          filename = decodeURIComponent(
            filenameStarMatch[1].replace(/["']/g, '')
          );
        } else {
          const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(
              filenameMatch[1].replace(/["']/g, '')
            );
          }
        }
      } else {
        filename = getFileNameFromUrl(resp.config.url);
      }
    }
    const blob = new Blob([resp.data], {
      type: resp.headers['content-type']
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    return resp;
  }
}
